import React, { useState, useRef, useEffect } from "react";
import styles from "./ImgPreview.module.less";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
interface ImgPreviewProps {
  list?: string[];
  pageState?: any;
  // setPageNo: () => void;
}

const ImgPreview: React.FC<ImgPreviewProps> = ({ list, pageState }: any) => {
  const numPages = list?.length || 0;
  const [currentPage, setCurrentPage] = useState(pageState?.pageNo || 1);
  const [inputPage, setInputPage] = useState("1");
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isScrolling = useRef(false);

  // 监听外部传入的 pageNo 变化
  useEffect(() => {
    if (pageState?.pageNo && pageState?.pageNo !== currentPage) {
      const targetPage = parseInt(pageState?.pageNo);
      if (
        !isNaN(targetPage) &&
        targetPage >= 1 &&
        targetPage <= (numPages || 1)
      ) {
        setCurrentPage(targetPage);
        setInputPage(targetPage.toString());
        scrollToPage(targetPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState, numPages]);

  // 监听滚动
  useEffect(() => {
    const handleScroll = () => {
      if (isScrolling.current) return;
      if (!pageRefs.current.length || !containerRef.current) return;

      const container = containerRef.current;
      const containerTop = container.scrollTop;
      const containerHeight = container.clientHeight;

      let current = 1;
      let maxVisibleRatio = 0;

      for (let i = 0; i < pageRefs.current.length; i++) {
        const page = pageRefs.current[i];
        if (page) {
          const pageTop = page.offsetTop;
          const pageBottom = pageTop + page.clientHeight;

          const visibleTop = Math.max(pageTop, containerTop);
          const visibleBottom = Math.min(
            pageBottom,
            containerTop + containerHeight,
          );
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          const visibleRatio = visibleHeight / page.clientHeight;

          if (visibleRatio > maxVisibleRatio) {
            maxVisibleRatio = visibleRatio;
            current = i + 1;
          }
        }
      }

      if (maxVisibleRatio > 0 && current !== currentPage) {
        setCurrentPage(current);
        setInputPage(current.toString());
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      setTimeout(() => handleScroll(), 100);

      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [numPages, currentPage]);

  // 滚动到指定页数
  const scrollToPage = (pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > (numPages || 1)) return;

    const pageIndex = pageNumber - 1;
    if (pageRefs.current[pageIndex] && containerRef.current) {
      isScrolling.current = true;

      const pageElement = pageRefs.current[pageIndex];
      const container = containerRef.current;

      if (pageElement) {
        const scrollTop = pageElement.offsetTop;
        container.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });

        setCurrentPage(pageNumber);
        setInputPage(pageNumber.toString());

        setTimeout(() => {
          isScrolling.current = false;
        }, 500);
      }
    }
  };

  // 处理键盘输入跳转
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const page = parseInt(inputPage);
      if (!isNaN(page) && page >= 1 && page <= (numPages || 1)) {
        scrollToPage(page);
      } else {
        setInputPage(currentPage.toString());
      }
    }
  };

  // 处理输入框变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setInputPage(value);
      const page = value === "" ? 1 : parseInt(value);
      if (page >= 1 && page <= (numPages || 1)) {
        scrollToPage(page);
      }
    }
  };

  // 失去焦点时处理
  const handleBlur = () => {
    const page = parseInt(inputPage);
    if (isNaN(page) || page < 1 || page > (numPages || 1)) {
      setInputPage(currentPage.toString());
    }
  };

  // 上一页
  const goToPreviousPage = () => {
    scrollToPage(currentPage - 1);
  };

  // 下一页
  const goToNextPage = () => {
    scrollToPage(currentPage + 1);
  };

  return (
    <div className={styles.container}>
      {/* 控制栏 */}
      {/* 页面导航 */}
      {!!numPages && (
        <div className="pdf-header">
          <div className="page-controls">
            <LeftOutlined
              onClick={goToPreviousPage}
              disabled={currentPage <= 1}
              className="leftBtn"
            />
            <div className="page-indicator">
              <input
                className="ipt"
                type="number"
                min={1}
                max={numPages || 1}
                value={inputPage}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                onBlur={handleBlur}
                placeholder="跳转到页"
              />
              <div className="line">/</div>
              <div>{numPages || 0}</div>
            </div>
            <RightOutlined
              onClick={goToNextPage}
              disabled={currentPage >= (numPages || 1)}
              className="rightBtn"
            />
          </div>
        </div>
      )}

      <div className="pdf-viewer" ref={containerRef}>
        {/* 
          1.后端能解析到图片
          2.解析不到图片但是不是微信路径
          3.是微信路径
        */}
        {list?.map((imgUrl: any, index: any) => (
          <div
            key={`page_wrapper_${index + 1}`}
            ref={(el) => {
              pageRefs.current[index] = el;
            }}
            className="page-wrapper"
            style={{ position: "relative" }}
          >
            <img src={imgUrl} alt={`第 ${index + 1} 页`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImgPreview;
